"use client";

import React, {useState, useRef, useEffect, useLayoutEffect} from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import InfoBox from "@/components/global/ui/InfoBox";
import CornerList from "@/components/global/ui/CornerList";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import {CTACornerButton} from "@/shadcn/ui/corner-button";
import {motion, useAnimationControls} from "framer-motion";
import gsap from "gsap";
import {ScrollTrigger} from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

import {PageKomplettausbildungSchema} from "@/sanity/pages/PageKomplettausbildungTemplate";

function AnimatedModuleCard({ children, index, cardRef }: { children: React.ReactNode, index: number, cardRef: (el: HTMLDivElement | null) => void }) {
  const controls = useAnimationControls();
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    controls.set({ opacity: 0, y: 40 });
  }, [controls]);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const st = ScrollTrigger.create({
      trigger: el,
      start: "top 80%",
      end: "bottom 20%",
      onEnter: () => controls.start({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } }),
      onLeave: () => {},
      onEnterBack: () => {},
      onLeaveBack: () => controls.start({ opacity: 0, y: 40, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } }),
      invalidateOnRefresh: true,
    });

    return () => st.kill();
  }, [controls]);

  return (
    <motion.div
      ref={(el) => {
        rootRef.current = el;
        cardRef(el);
      }}
      className="flex items-start justify-center w-full overflow-clip"
      animate={controls}
      initial={{ opacity: 0, y: 40 }}
    >
      {children}
    </motion.div>
  );
}

export default function KomplettausbildungModulesSection({
  data,
  className
}: {
  data: PageKomplettausbildungSchema;
  className?: string;
}) {
  const [activeModuleIndex, setActiveModuleIndex] = useState(0);
  const moduleRefs = useRef<(HTMLDivElement | null)[]>([]);

  const moduleColors = [
    "#D9F9B9",
    "#E9B9F9",
    "#F9E0B9",
    "#B9C0F9",
    "#F9B9B9",
    "#B9F0F9",
    "#F4F9B9",
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = moduleRefs.current.findIndex(ref => ref === entry.target);
            if (index !== -1) {
              setActiveModuleIndex(index);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '-20% 0px -40% 0px'
      }
    );

    moduleRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      observer.disconnect();
    };
  }, [data?.modulesItems, data?.modulesCTA]);

  return (
    <section className={cn(
      "relative overflow-clip bg-[#0A1A1D] py-[13rem] lg:py-[20rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
        style="light"
      />
      <DecorationSquares
        className="top-0"
        style="green"
      />

      <div className="container relative flex flex-col items-center">
        <div className="w-[min(187.75rem,100%)] flex flex-col gap-[5rem] lg:gap-[15rem]">
          <div className="flex flex-col items-center lg:items-start">
            <Chip
              image={data?.modulesChip?.icon}
              text={data?.modulesChip?.text}
            />

            {data?.modulesTitle && (
              <div className="richtext lg:richtext-start prose-headings:text-white prose-headings:w-[min(85rem,100%)] prose-p:text-white/60 prose-p:w-[min(85rem,100%)]">
                <PortableText
                  value={data.modulesTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {data?.modulesItems && (
            <div className="flex flex-col lg:flex-row items-center lg:items-start lg:justify-between gap-[3rem]">
              {/* navigation */}
              <div className="hidden lg:flex flex-col sticky top-[20rem] pb-[13rem] lg:pb-[20rem]">
                {data?.modulesItems?.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-start min-h-[4.675rem] flex-shrink-0 pl-[1.5rem] relative"
                  >
                    {activeModuleIndex === index && (
                      <motion.div
                        layoutId="module-nav-indicator"
                        className="absolute left-0 top-0 h-full w-[2px]"
                        style={{
                          backgroundColor: moduleColors[index % moduleColors.length]
                        }}
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 30
                        }}
                      />
                    )}
                    <p
                      className="mb-0 leading-none text-[2rem] transition-colors duration-300"
                      style={{
                        color: activeModuleIndex === index
                          ? moduleColors[index % moduleColors.length]
                          : 'rgba(255, 255, 255, 0.4)'
                      }}
                    >
                      {index + 1}. {item?.name}
                    </p>
                  </div>
                ))}

                {data?.modulesCTA && (
                  <div className="flex items-center justify-start min-h-[4.675rem] flex-shrink-0 pl-[1.5rem] relative">
                    {activeModuleIndex === data.modulesItems.length && (
                      <motion.div
                        layoutId="module-nav-indicator"
                        className="absolute left-0 top-0 h-full w-[2px]"
                        style={{
                          backgroundColor: "#D9F9B9"
                        }}
                        initial={false}
                        transition={{
                          type: "spring",
                          stiffness: 500,
                          damping: 30
                        }}
                      />
                    )}
                    <p
                      className="mb-0 leading-none text-[2rem] transition-colors duration-300"
                      style={{
                        color: activeModuleIndex === data.modulesItems.length
                          ? "#D9F9B9"
                          : 'rgba(255, 255, 255, 0.4)'
                      }}
                    >
                      {data.modulesItems.length + 1}. {data.modulesCTA.cta?.title}
                    </p>
                  </div>
                )}
              </div>

              <div className="w-[min(137.5rem,100%)] flex flex-col gap-[6rem] lg:gap-[9rem] relative ">
                {data?.modulesItems?.map((item, index) => (
                  <AnimatedModuleCard
                    key={index}
                    index={index}
                    cardRef={(el) => (moduleRefs.current[index] = el)}
                  >
                    <p
                      className="sticky top-[5rem] lg:top-[20rem] -translate-y-px translate-x-px mb-0 min-h-[6rem] w-[3.25rem] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]"
                      style={{backgroundColor: moduleColors[index % moduleColors.length]}}
                    >
                      {index + 1}
                    </p>

                    <div
                      className="flex flex-col lg:flex-row items-center lg:items-stretch lg:justify-between gap-[3rem] lg:gap-[2rem] bg-[#112628] p-[2.5rem] sm:p-[3rem] md:p-[4rem] lg:p-[1rem] w-[min(65rem,100%)] lg:w-auto lg:grow"
                      style={{borderLeft: `2px solid ${moduleColors[index % moduleColors.length]}`}}
                    >
                      <div className="flex flex-col justify-between gap-[3rem] lg:gap-[7rem] grow lg:py-[5rem] lg:pl-[5rem]">
                        <div className="flex flex-col ">
                          <h3 className="text-white text-[3rem] sm:text-[4rem] lg:text-[5rem] !font-geist !mb-[.75rem]">{item?.name}</h3>
                          <p className="text-white/60 text-[2.25rem] font-light mb-[2rem]">{item?.subtitle}</p>

                          {item?.chips && (
                            <div className="flex items-center gap-[1rem]">
                              {item?.chips?.map((chip, chipIndex) => (
                                <div
                                  key={chipIndex}
                                  className="px-[2rem] rounded-full min-h-[5.25rem] py-[1rem] flex items-center justify-center"
                                  style={{
                                    border: `1px solid ${moduleColors[index % moduleColors.length]}`,
                                    color: moduleColors[index % moduleColors.length],
                                    backgroundColor: `color-mix(in srgb, ${moduleColors[index % moduleColors.length]} 5%, transparent)`,
                                  }}
                                >
                                  <p className="mb-0 leading-none font-geistmono text-[1.75rem]">{chip?.text}</p>
                                </div>
                              ))}
                            </div>
                          )}

                          <hr className="border-t border-t-white/10 w-full mt-[2rem] lg:mt-[3rem]" />
                        </div>

                        <div className="flex flex-col">
                          {item?.listTitle && (
                            <p className="mb-[2rem] font-geistmono text-white/50 uppercase text-[2rem]">{item.listTitle}</p>
                          )}

                          <CornerList
                            items={item?.list}
                            color={moduleColors[index % moduleColors.length]}
                          />

                          {item?.outcome?.title && item?.outcome?.text && (
                            <InfoBox
                              className="mt-[3rem]"
                              title={item.outcome.title}
                              text={item.outcome.text}
                              color={moduleColors[index % moduleColors.length]}
                            />
                          )}
                        </div>
                      </div>

                      {item?.image && (
                        <div className="!w-[min(60.75rem,100%)] flex-shrink-0">
                          <Image
                            className="!w-full lg:!h-full object-cover"
                            src={item.image}
                            quality={95}
                            alt={item?.name ?? ""}
                          />
                        </div>
                      )}
                    </div>
                  </AnimatedModuleCard>
                ))}

                {data?.modulesCTA && (
                  <AnimatedModuleCard
                    index={data.modulesItems.length}
                    cardRef={(el) => (moduleRefs.current[data.modulesItems.length] = el)}
                  >
                    <p
                      className="-translate-y-px translate-x-px mb-0 min-h-[6rem] w-[3.25rem] flex-shrink-0 [clip-path:polygon(0_0,100%_0,100%_100%,1.5rem_100%,0_calc(100%-1.5rem))] leading-none flex items-center justify-center font-geistmono font-medium text-black text-[2rem]"
                      style={{backgroundColor: "#D9F9B9"}}
                    >
                      {data.modulesItems.length + 1}
                    </p>

                    <div
                      className="flex flex-col lg:flex-row items-center lg:justify-between gap-[3rem] bg-[#112628] w-[min(65rem,100%)] lg:w-auto lg:grow pb-[2rem] px-[2rem] sm:px-[3rem] pt-[3rem] lg:py-[1rem] lg:pr-[1rem] lg:pl-[3rem]"
                      style={{borderLeft: `2px solid #D9F9B9`}}
                    >
                      {data?.modulesCTAText && (
                        <div className="richtext lg:richtext-start prose-p:text-white/80 prose-p:w-[min(60rem,100%)]">
                          <PortableText
                            value={data.modulesCTAText}
                            components={richTextComponent}
                          />
                        </div>
                      )}

                      <CTACornerButton
                        className="flex-shrink-0"
                        data={data.modulesCTA}
                      />
                    </div>
                  </AnimatedModuleCard>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
